/**
 * Sprint M5.32 (27.07.2026) + M5.32-Hotfix (28.07.2026) + M5.32-Hotfix-Fix-2 (28.07.2026)
 *
 * Provider-Confirm-Box.
 *
 * Sprint M5.32-Hotfix-Fix-2 (28.07.2026):
 *  - providerCopy-Map offener formuliert (GMX sortiert nicht zuverlaessig
 *    in „Werbung", also keine Hard-Anweisungen mehr, sondern ehrliche
 *    Suchhinweise).
 *  - Universaltipp-Komponente ({Suche + Drag/Kein-Spam}) rendert immer.
 *  - iCloud/me.com bekommen einen Warnblock (Domain-Reputations-Risiko)
 *    mit CTA „Andere E-Mail-Adresse verwenden" → `/lp/flex4success?change_email=true`.
 *  - Kein Hard-Block, kein Mail-Versand, keine Architektur-Aenderung.
 *
 * Sprint M5.32-Hotfix (28.07.2026):
 *  - In-App-Reconfirm-Button entfernt (bypassed KlickTipp-DOI-Tracking).
 *  - Re-Trigger laufen ausschliesslich ueber KlickTipp (M5.28d).
 */
import React, { useMemo } from "react";
import { Sparkles, MailCheck, Search, MoveRight, AlertTriangle, ArrowRight } from "lucide-react";

/** Erkennt den Mail-Provider anhand der Domain. */
export function detectProvider(emailAddress) {
  if (!emailAddress || !emailAddress.includes("@")) return "generic";
  const domain = emailAddress.split("@")[1].toLowerCase();
  if (/gmail|googlemail/.test(domain)) return "gmail";
  if (/gmx/.test(domain)) return "gmx";
  if (/yahoo/.test(domain)) return "yahoo";
  if (/t-online/.test(domain)) return "t-online";
  if (/icloud|me\.com|mac\.com/.test(domain)) return "icloud";
  if (/hotmail|outlook|live|msn/.test(domain)) return "outlook";
  if (/web\.de/.test(domain)) return "webde";
  if (/aol/.test(domain)) return "aol";
  if (/bluewin/.test(domain)) return "bluewin";
  return "generic";
}

/**
 * Sprint M5.32-Hotfix-Fix-2: Offene, ehrliche Copy statt Hard-Anweisungen.
 * GMX/Yahoo/T-Online sortieren nicht immer sauber in ihre Werbe-Ordner.
 * Deshalb: Kandidaten-Ordner nennen + Suche + „Kein Spam" markieren empfehlen.
 */
const PROVIDER_COPY = {
  gmail: {
    label: "Gmail / Googlemail",
    hint: 'Schau oben in die Tabs \u201EPromotions\u201C und \u201EUpdates\u201C \u2014 die Mail landet dort oft automatisch.',
  },
  gmx: {
    label: "GMX",
    hint: 'Pr\u00FCf neben dem Posteingang auch die Ordner \u201EWerbung\u201C und \u201ESpam\u201C \u2014 die Sortierung ist bei GMX nicht immer verl\u00E4sslich.',
  },
  yahoo: {
    label: "Yahoo",
    hint: 'Wirf einen Blick in die Ordner \u201EBulk\u201C und \u201ESpam\u201C, wenn im Posteingang nichts ankommt.',
  },
  "t-online": {
    label: "T-Online",
    hint: 'Schau in die Ordner \u201EJunk\u201C und \u201ESpam\u201C \u2014 T-Online filtert neue Absender oft dorthin.',
  },
  icloud: {
    label: "iCloud / me.com / mac.com",
    hint: 'Pr\u00FCf den Ordner \u201EWerbung\u201C und die iCloud-Junk-Ablage. Apple filtert neue Absender teils sehr streng.',
  },
  outlook: {
    label: "Outlook / Hotmail / Live",
    hint: 'Schau in \u201EJunk-E-Mail\u201C und \u201EAndere\u201C \u2014 Outlook trennt Absender manchmal in einen zweiten Posteingang.',
  },
  webde: {
    label: "Web.de",
    hint: 'Pr\u00FCf die Ordner \u201EWerbung\u201C und \u201ESpam\u201C, wenn der Posteingang leer bleibt.',
  },
  aol: {
    label: "AOL",
    hint: 'Wirf einen Blick in den Spam-Ordner \u2014 AOL filtert neue Absender h\u00E4ufig dorthin.',
  },
  bluewin: {
    label: "Bluewin",
    hint: 'Schau in den Spam-Ordner \u2014 Bluewin filtert unbekannte Absender oft dorthin.',
  },
  generic: {
    label: "Dein Mail-Anbieter",
    hint: 'Pr\u00FCf neben dem Posteingang auch die Werbe- und Spam-Ordner.',
  },
};

/** Sprint M5.32-Hotfix-Fix-2 — Universaltipp: Such-Hinweis + Kein-Spam-Hinweis. */
function UniversalTips() {
  return (
    <div
      className="mt-4 rounded-xl bg-white border border-slate-200 p-4 space-y-3"
      data-testid="provider-universal-tips"
    >
      <div className="flex items-start gap-3">
        <Search className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-600 font-semibold">
            Tipp 1 &middot; Suchen statt scrollen
          </p>
          <p className="text-sm sm:text-base text-slate-800 mt-0.5" data-testid="universal-tip-search-text">
            Nutze die Postfach-Suche nach{" "}
            <span className="font-semibold">Flex4Success</span> oder{" "}
            <span className="font-semibold">effektiv-kommunizieren</span> — damit findest du die Mail in jedem Ordner.
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <MoveRight className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-600 font-semibold">
            Tipp 2 &middot; Mail sichtbar halten
          </p>
          <p className="text-sm sm:text-base text-slate-800 mt-0.5" data-testid="universal-tip-drag-text">
            Wenn du sie gefunden hast: markiere sie als{" "}
            <span className="font-semibold">&bdquo;Kein Spam&ldquo;</span> oder ziehe sie in den Posteingang —
            dann kommen auch die Folgemails direkt bei dir an.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Sprint M5.32-Hotfix-Fix-2 — iCloud/me.com-Warnblock.
 *
 * Apple filtert neue Absender-Domains besonders streng (Domain-Reputation).
 * Kein Hard-Switch, kein Blocking \u2014 wir zeigen ehrlich das Risiko und
 * bieten den Alternativ-Weg an.
 */
function AppleMailWarning({ changeEmailUrl }) {
  return (
    <div
      className="mt-5 rounded-xl border border-amber-300 bg-amber-50 p-4"
      data-testid="provider-icloud-warning"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wider text-amber-800 font-semibold">
            Hinweis für iCloud, me.com &amp; mac.com
          </p>
          <p className="text-sm sm:text-base text-amber-900 mt-1 leading-relaxed">
            Apple filtert neue Absender besonders streng. Wenn die Mail nicht innerhalb weniger Minuten
            im Posteingang oder Werbe-Ordner ankommt, nutze bitte eine Zweit-Adresse
            (z. B. Gmail oder GMX) — damit landet dein Zugang zuverlässig bei dir.
          </p>
          <a
            href={changeEmailUrl}
            className="mt-3 inline-flex items-center gap-2 rounded-xl bg-amber-600 hover:bg-amber-700 px-4 py-2.5 text-sm sm:text-base font-semibold text-white shadow-md shadow-amber-600/30 transition-colors"
            data-testid="provider-icloud-change-email-btn"
          >
            Andere E-Mail-Adresse verwenden
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}

export default function ProviderConfirmBox({ email, changeEmailUrl = "/lp/flex4success?change_email=true" }) {
  const provider = useMemo(() => detectProvider(email), [email]);
  const providerCopy = PROVIDER_COPY[provider] || PROVIDER_COPY.generic;
  const isApple = provider === "icloud";

  return (
    <div
      className="mx-auto max-w-lg rounded-2xl border border-orange-200 bg-orange-50/60 p-6 sm:p-8 shadow-sm"
      data-testid="provider-confirm-box"
    >
      <div className="flex items-center gap-2 text-orange-700 mb-3">
        <Sparkles className="w-5 h-5" />
        <h2 className="text-lg sm:text-xl font-bold" data-testid="provider-box-title">
          Du bist drin. Fehlt nur noch ein Klick.
        </h2>
      </div>

      <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
        Damit du deine Vorteile aktivierst, haben wir dir gerade eine Bestätigungs-Mail an{" "}
        <strong className="text-slate-900" data-testid="provider-box-email">{email}</strong> geschickt.
      </p>

      <div
        className="mt-4 rounded-xl bg-white border border-orange-200 p-4 flex items-start gap-3"
        data-testid="provider-hint-box"
      >
        <MailCheck className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs uppercase tracking-wider text-orange-700 font-semibold">
            {providerCopy.label}
          </p>
          <p className="text-sm sm:text-base font-medium text-slate-900 mt-0.5" data-testid="provider-hint-text">
            {providerCopy.hint}
          </p>
        </div>
      </div>

      <UniversalTips />

      {isApple && <AppleMailWarning changeEmailUrl={changeEmailUrl} />}

      <p className="mt-5 text-xs text-slate-500 text-center">
        Ein Klick — und du bist Pionier mit allen Vorteilen.
      </p>
    </div>
  );
}
